import React, { useContext, useEffect, useState } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';

import { CenteredLoadingSpinner, Table } from '@ole-ui/ole-ui-components';
import { handleError } from 'services/ErrorService';
import GenericViewError from 'components/Error/GenericViewError';
import { storesContext } from 'stores';
import CourseProgressTableStore from './store';
import './styles.scss';
import UserMetricsEmptyState from '../EmptyState';

const CourseProgressTable = (props) => {
  const { courseType } = props;
  const { t } = useTranslation();
  const { progressStore, organizationStore, catalogStore, uiStore } =
    useContext(storesContext);
  const store = useLocalStore(
    () =>
      new CourseProgressTableStore(
        progressStore,
        organizationStore,
        catalogStore,
      ),
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        await store.getReporteesProgress(
          organizationStore.organization.id,
          organizationStore.selectedUsersByManagerList,
          courseType,
          controller.signal,
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }, 1000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
      setIsLoading(false);
    };
  }, [
    organizationStore.organization.id,
    organizationStore.selectedUsersByManagerList,
  ]);

  useEffect(() => {
    store.courseSKUTranslation = t('Course SKU: ');
    store.versionTranslation = t(', Version: ');
    store.userCourseTranslation =
      courseType === 'module' ? t('User/Lesson ') : t('User/Course ');
  }, [uiStore.currentLanguage]);

  const renderEmptyState = () => {
    return (
      <UserMetricsEmptyState
        selectedCount={organizationStore.selectedUsersByManagerList?.length}
      />
    );
  };

  if (isLoading) return <CenteredLoadingSpinner />;

  return (
    <React.Fragment>
      <div className="table-horiz-scroll mgmt-course-progress-table">
        <Table
          ariaLabel={t('Course Progress table')}
          data={store.transformedData}
          columns={store.tableColumns}
          variant="compact"
          renderEmptyState={renderEmptyState}
        />
      </div>
    </React.Fragment>
  );
};

CourseProgressTable.propTypes = {
  courseType: PropTypes.string,
};

CourseProgressTable.defaultProps = {
  courseType: null,
};

export default withErrorBoundary(
  observer(CourseProgressTable),
  GenericViewError,
  handleError,
);
